// sends GET requests
/**
 * handles an authenticated GET request with success and error handling functions
 * @param {string} fullUrl : url with query params
 * @param {array} knownErrors : strings to backend errors
 * @param {function} onSuccess : defined before/at function call to handle success with api response as input
 * @param {function} onError : defined before/at function call to handle errors with error as input
 * @param {string} auth : user auth token - blank if none needed
 * @param {object} options : optional settings
 * @param {number} options.timeout : request timeout in ms
 * @returns {bool} if success or error
*/
export async function fetchGET(fullUrl, knownErrors = [], onSuccess = () => {}, onError = () => {}, auth = ``, options = {}) {
    if (auth && typeof auth === `object`) {
        options = auth;
        auth = ``;
    }

    options = options || {};

    // for exponential backoff
    let numRetries = 0; // initial
    let maxRetries = 3;
    let delay = 500; // ms
    let timeout = Number.isFinite(options.timeout) ? options.timeout : 45000; // 45s default

    // Helper function to create a timeout promise
    const timeoutPromise = (ms) =>
        new Promise((_, reject) =>
            setTimeout(() => reject(new Error(`Request timed out after ${ms}ms`)), ms)
        );

    while (numRetries <= maxRetries) {
        try {
            console.log('GET API:', fullUrl);
    
            // API call data (GET)
            let response = await Promise.race([
                fetch(fullUrl, { // full url contains query strings too
                    headers: {
                        // TEMPORARY: FAKE AUTH TOKEN
                        'Authorization': `Bearer ${auth}`,
                        //'Authorization': `Bearer ${await getLocalStorageItem("auth")}`,
                    },
                    method: "GET"
                }),
                timeoutPromise(timeout),
            ]);
    
            // ERROR: Check HTTP-level errors
            if (!response.ok) {
                throw new Error(`HTTP status ${response.status}`);
            }
    
            let responseData = await response.json();
    
            // ERROR: Check for known errors
            if (knownErrors.includes(responseData.payload)) {
                throw new Error(`Backend error: ${responseData.payload}`);
            }
    
            // If successful request
            if (responseData || responseData === 0) {
                onSuccess(responseData); // callback
    
                return true;
            } 
            // ERROR: unknown
            else { 
                throw new Error('Unknown error');
            }
        } catch (error) {
            console.error(error); // log error
    
            numRetries++; // iter for exponential backoff
    
            // too many failed calls or known error
            if ((numRetries > maxRetries) || (error.message.includes('Backend error:')) || error.message.includes(`401`)) {
                // callback
                onError(error);
    
                return false;
            }
    
            // Calculate next delay with exponential backoff and jitter
            delay *= 2 * (1 + Math.random());
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
}

// sends POST requests
/**
 * handles an authenticated POST request with success and error handling functions
 * @param {string} fullUrl : url with query params
 * @param {object} body : body object
 * @param {array} knownErrors : strings to backend errors
 * @param {function} onSuccess : defined before/at function call to handle success with api response as input
 * @param {function} onError : defined before/at function call to handle errors with error as input
 * @param {string} auth : user auth token - blank if none needed
 * @param {object} options : optional settings
 * @param {number} options.timeout : request timeout in ms
 * @returns {bool} if success or error
*/
export async function fetchPOST(fullUrl, body, knownErrors = [], onSuccess = () => {}, onError = () => {}, auth = ``, options = {}) {
    if (auth && typeof auth === `object`) {
        options = auth;
        auth = ``;
    }

    options = options || {};

    // for exponential backoff
    let numRetries = 0; // initial
    let maxRetries = 3;
    let delay = 500; // ms
    let timeout = Number.isFinite(options.timeout) ? options.timeout : 45000; // 45s default

    // Helper function to create a timeout promise
    const timeoutPromise = (ms) =>
        new Promise((_, reject) =>
            setTimeout(() => reject(new Error(`Request timed out after ${ms}ms`)), ms)
        );

    while (numRetries <= maxRetries) {
        try {
            console.log('POST API:', fullUrl);

            // Perform the POST request.
            let response = await Promise.race([
                fetch(fullUrl, {
                    headers: {
                        // TEMPORARY: FAKE AUTH TOKE
                        'Authorization': `Bearer ${auth}`,
                        //'Authorization': `Bearer ${await getLocalStorageItem("auth")}`,
                        'Content-Type': 'application/json', // Ensure JSON content type for POST requests
                    },
                    method: "POST",
                    body: JSON.stringify(body),
                }),
                timeoutPromise(timeout),
            ]);

            // Check for HTTP-level errors
            if (!response.ok) {
                console.log(response);
                throw new Error(`HTTP status ${response.status}`);
            }

            const responseData = await response.json();

            // Check for known API errors dynamically by verifying if the payload is in the knownErrors array
            if (knownErrors.includes(responseData.payload)) {
                throw new Error(`Backend error: ${responseData.payload}`);
            }

            // If the response is valid, run the success callback with the response data
            if (responseData) {
                onSuccess(responseData); // callback
    
                return true;
            } 
        } catch (error) {
            console.error(error); // log error

            numRetries++; // iter for exponential backoff

            // too many failed calls or known error
            if ((numRetries > maxRetries) || (error.message.includes('Backend error:')) || error.message.includes(`401`)) {
                // callback
                onError(error);
    
                return false;
            }

            // Calculate next delay with exponential backoff and jitter
            delay *= 2 * (1 + Math.random());
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
}

// runs polling
/**
 * Create a poller that runs a handler on a schedule.
 *
 * Modes:
 *  - "static": runs every `initialIntervalMs`
 *  - "growing": waits `initialIntervalMs`, then multiplies by `multiplier` each cycle
 *
 * Behavior:
 *  - Optional `initialHandler`: if provided, it runs ONCE immediately when `start()` is called,
 *    before any waiting or first regular handler trigger. It may be async (awaits allowed).
 *  - If `runImmediately` is false (default), AFTER the optional `initialHandler`, the poller
 *    waits the interval before the first regular handler run.
 *  - If `runImmediately` is true, AFTER the optional `initialHandler`, the regular handler
 *    fires right away, then future runs follow the schedule.
 *  - If the regular `handler` is async, the next wait starts *after* it completes.
 *  - If `maxTimeMs` is set, total elapsed time since `start()` (including `initialHandler`)
 *    is counted; polling stops once elapsed >= `maxTimeMs`.
 *
 * @param {Object} opts
 * @param {"static"|"growing"} [opts.mode="static"]
 * @param {number} [opts.initialIntervalMs=32000]
 * @param {number} [opts.multiplier=1.5]         // only used in "growing"
 * @param {number} [opts.maxIntervalMs=Infinity] // cap for growing intervals
 * @param {boolean} [opts.runImmediately=false]  // true = run once immediately (after initialHandler)
 * @param {number} [opts.maxTimeMs=0]            // 0/falsy disables max-time stopping
 * @param {(state: {runCount:number, lastDelay:number, elapsedMs:number}) => (void|Promise<void>)} handler
 * @param {(state: {elapsedMs:number}) => (void|Promise<void>)} [initialHandler] // optional one-time pre-run
 */
export function createPoller(
    {
        mode = "static",
        initialIntervalMs = 32000,
        multiplier = 1.5,
        maxIntervalMs = Infinity,
        runImmediately = false,
        maxTimeMs = 0,
    } = {},
    handler,
    initialHandler = null
) {
    if (typeof handler !== "function") {
        throw new TypeError("createPoller requires a handler function as the second argument");
    }
    if (initialHandler != null && typeof initialHandler !== "function") {
        throw new TypeError("initialHandler must be a function if provided");
    }

    let timerId = null;
    let running = false;
    let nextDelay = Math.max(0, initialIntervalMs);
    let runCount = 0;
    let startedAt = 0;   // timestamp when polling started
    let lastElapsed = 0; // elapsed ms at the time of the last run

    function clearTimer() {
        if (timerId !== null) {
            clearTimeout(timerId);
            timerId = null;
        }
    }

    function elapsedMs() {
        return startedAt ? Date.now() - startedAt : 0;
    }

    function withinMaxTime() {
        if (!maxTimeMs) return true; // disabled
        return elapsedMs() < maxTimeMs;
    }

    function scheduleNextTick() {
        if (!withinMaxTime()) {
            stop();
            return;
        }
        let delay = nextDelay;
        if (maxTimeMs) {
            const remaining = maxTimeMs - elapsedMs();
            if (remaining <= 0) {
                stop();
                return;
            }
            delay = Math.min(delay, remaining);
        }
        timerId = setTimeout(tick, delay);
    }

    async function tick() {
        if (!running) return;

        lastElapsed = elapsedMs();

        try {
            // Regular handler (async allowed)
            await handler({ runCount, lastDelay: nextDelay, elapsedMs: lastElapsed });
            runCount += 1;
        } catch (err) {
            console.error("Poller handler error:", err);
        }

        // Decide next delay after the work completes
        if (mode === "growing") {
            nextDelay = Math.min(Math.round(nextDelay * multiplier), maxIntervalMs);
        } else {
            nextDelay = initialIntervalMs;
        }

        if (running) scheduleNextTick();
    }

    function start() {
        if (running) return;
        running = true;
        startedAt = Date.now();
        nextDelay = Math.max(0, initialIntervalMs);

        // Run optional initial handler first (async-safe), then proceed according to runImmediately.
        (async () => {
            if (initialHandler) {
                try {
                    await initialHandler({ elapsedMs: elapsedMs() });
                } catch (err) {
                    console.error("Initial handler error:", err);
                }
                // After initial handler, re-check max time
                if (!withinMaxTime()) {
                    stop();
                    return;
                }
            }

            if (runImmediately) {
                // Fire a regular tick immediately (counts against maxTimeMs).
                await tick();
            } else {
                // Wait first interval, then fire the regular handler.
                scheduleNextTick();
            }
        })();
    }

    function stop() {
        running = false;
        clearTimer();
    }

    function reset() {
        // Reset schedule and counters; does NOT restart automatically
        nextDelay = Math.max(0, initialIntervalMs);
        runCount = 0;
        startedAt = 0;
        lastElapsed = 0;
        clearTimer();
        running = false;
    }

    return {
        start,
        stop,
        reset,
        isRunning: () => running,
        getNextDelay: () => nextDelay,
        getRunCount: () => runCount,
        getElapsedMs: () => elapsedMs(),
    };
}
